<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { toast } from '../js/toast.js';
  import { spinner } from '../js/spinner.js';
  import Modal from './Modal.svelte';
  import Checkout from './Checkout.svelte';
  import type { ApiResponse } from '../js/apiTypes.js';

  export let showModal = false;
  export let addons: Array<{ uuid: string; name: string; description?: string; price: number }> = [];

  const dispatch = createEventDispatcher();

  let selectedAddon: { uuid: string; name: string; price: number } | null = null;
  let displayCheckout = false;
  let modalWidth = 800;

  function selectAddon(addon: { uuid: string; name: string; price: number }) {
    selectedAddon = addon;
    displayCheckout = true;
  }

  async function handleCheckoutSubmit(paymentMethodId: string): Promise<{ success?: boolean; message?: string }> {
    if (!selectedAddon) return { success: false, message: 'No add-on selected' };
    spinner.show();
    try {
      const teamUuid = $auth.user?.active_team_uuid;
      if (!teamUuid) {
        return { success: false, message: 'No active team found.' };
      }
      const response = (await api.purchaseTeamAddon(teamUuid, selectedAddon.uuid, paymentMethodId)) as {
        success?: boolean;
        message?: string;
      };
      if (response.success) {
        toast.showNotification(true, 'Add-on purchased successfully!');
        displayCheckout = false;
        showModal = false;
        dispatch('purchased');
      } else {
        toast.showNotification(false, response.message || 'Failed to purchase add-on');
      }
      return response;
    } catch (error) {
      toast.showNotification(false, 'Error purchasing add-on');
      console.error(error);
      return { success: false };
    } finally {
      spinner.hide();
    }
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }
</script>

<Modal bind:showModal bind:modalWidth modalClass="addon-purchase-modal">
  <div slot="header">Purchase Add-on Service</div>
  <div slot="body">
    <p class="modal-description">
      Fixed-price professional services billed to your team. Our team will follow up after purchase.
    </p>

    {#if addons.length === 0}
      <p class="empty-copy">No add-on services are available right now.</p>
    {:else}
      <div class="addons-list">
        {#each addons as addon (addon.uuid)}
          <div class="addon-card">
            <div class="addon-header">
              <h4 class="addon-name">{addon.name}</h4>
              <div class="addon-price">{formatPrice(addon.price)}</div>
            </div>
            {#if addon.description}
              <p class="addon-description">{addon.description}</p>
            {/if}
            <button type="button" class="addon-select-button" on:click={() => selectAddon(addon)}>
              Purchase
            </button>
          </div>
        {/each}
      </div>
    {/if}
  </div>
  <div slot="footer">
    <button type="button" class="close-button" on:click={() => (showModal = false)}>Close</button>
  </div>
</Modal>

<Checkout bind:displayCheckout onSubmitPaymentMethod={handleCheckoutSubmit} />

<style>
  :global(.ui.modal.addon-purchase-modal) {
    width: 800px !important;
    min-width: 800px !important;
  }

  .modal-description,
  .empty-copy {
    color: var(--grey-secondary);
    margin-bottom: var(--space-5);
    line-height: 1.6;
  }

  .addons-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--space-5);
    width: 100%;
  }

  @media (max-width: 768px) {
    :global(.ui.modal.addon-purchase-modal) {
      width: 100% !important;
      min-width: 0 !important;
    }
  }

  .addon-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    box-shadow: var(--shadow-card);
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .addon-card:hover {
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .addon-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
  }

  .addon-name {
    font-size: var(--text-lg);
    font-weight: 500;
    color: var(--grey);
    margin: 0;
  }

  .addon-price {
    font-size: var(--text-lg);
    font-weight: 500;
    color: var(--primary);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .addon-description {
    margin: 0;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  .addon-select-button {
    width: 100%;
    padding: var(--space-2) var(--space-4);
    background: var(--primary);
    color: var(--color-bright);
    border: 0;
    border-radius: var(--radius-button);
    font-weight: 500;
    cursor: pointer;
    box-shadow: var(--shadow-button);
    transition: background-color 0.2s;
  }

  .addon-select-button:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .addon-select-button:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .close-button {
    padding: var(--space-2) var(--space-5);
    background: var(--surface);
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-button);
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
  }

  .close-button:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
</style>
